import {useContext, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import AuthLayout from "../../layout/AuthLayout.jsx";
import AuthImage from "../../mosaic/images/auth-image.jpg";
import FormContainer from "../../components/form/FormContainer.jsx";
import FormControl from "../../components/form/FormControl.jsx";
import FormCheckbox from "../../components/form/FormCheckbox.jsx";
import FormButton from "../../components/form/FormButton.jsx";
import LoadingComponent from "../../components/LoadingComponent.jsx";
import FormError from "../../components/form/FormError.jsx";
import useDownloadState from "../../api/utilities/useDownloadState.js";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ExternalAuthenticationApi} from "../../api/authority/generated/index.ts";
import Or from "../../components/Or.jsx";
import FacebookLoginButton from "../../utils/brands/FacebookLoginButton.jsx";
import GoogleLoginButton from "../../utils/brands/GoogleLoginButton.jsx";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import toast from "react-hot-toast";
import {useRedirectAuthenticatedEffect} from "../../components/auth/useRedirectAuthenticatedEffect.js";
import {getPasswordMismatchError} from "./authValidation.js";

function createError(message) {
    return {message};
}

export default function AddonAuth() {
    useRedirectAuthenticatedEffect();

    const {t} = useTranslation();
    const navigate = useNavigate();
    const {getAuthorityConfiguration, setCurrentIdentity} = useContext(GlobalContext);
    const {et} = useErrorTranslation();
    const [state, setDownloading, , setError] = useDownloadState();
    const [step, setStep] = useState('email');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordAgain, setPasswordAgain] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [agreementTermsOfService, setAgreementTermsOfService] = useState(false);
    const [agreementPrivacyPolicy, setAgreementPrivacyPolicy] = useState(false);

    const authorityConfiguration = getAuthorityConfiguration();
    const basePath = authorityConfiguration.basePath;

    const trimmedEmail = useMemo(() => email.trim(), [email]);

    const navigateAfterAuthentication = (identity) => {
        if (identity?.user?.newPasswordRequired) {
            navigate('/onboarding/password');
            return;
        }

        if (
            identity?.user?.firstName === ''
            || identity?.user?.lastName === ''
            || identity?.user?.phone === ''
        ) {
            navigate('/onboarding/info');
            return;
        }

        navigate('/onboarding/addon-project');
    };

    const postJson = async (path, body) => {
        const response = await fetch(`${basePath}${path}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });

        const data = await response.clone().json().catch(() => null);

        if (!response.ok) {
            if (data?.message) {
                throw createError(data.message);
            }

            throw createError('Request failed.');
        }

        return {
            response,
            data,
        };
    };

    const validateRegistration = () => {
        if (firstName.trim() === '') return t('validation.first-name-required');
        if (lastName.trim() === '') return t('validation.last-name-required');

        const phoneWithoutSpaces = phone.replace(/\s+/g, '');
        if (phoneWithoutSpaces.length < 9) return t('validation.phone-min-length');
        if (phoneWithoutSpaces.length > 16) return t('validation.phone-max-length');
        if (password.trim() === '') return t('auth.must-provide-password');
        const passwordMismatchError = getPasswordMismatchError(password, passwordAgain, t);
        if (passwordMismatchError !== null) return passwordMismatchError;
        if (!agreementTermsOfService || !agreementPrivacyPolicy) return t('validation.fill_required_fields');

        return null;
    };

    const handleEmailSubmit = async () => {
        if (trimmedEmail === '') {
            setError(t('auth.must-provide-valid-email'));
            return;
        }

        setDownloading(true);
        setError(null);

        try {
            const {data} = await postJson('/api/v1/auth/check-email', {
                email: trimmedEmail,
            });

            setStep(data?.exists ? 'login' : 'signup');
        } catch (requestError) {
            setError(requestError);
        } finally {
            setDownloading(false);
        }
    };

    const handleLoginSubmit = async () => {
        if (password.trim() === '') {
            setError(t('auth.must-provide-password'));
            return;
        }

        setDownloading(true);
        setError(null);

        try {
            const {data} = await postJson('/api/v1/auth/sign-in', {
                email: trimmedEmail,
                password,
            });

            setCurrentIdentity(data);
            navigateAfterAuthentication(data);
        } catch (requestError) {
            setError(requestError);
        } finally {
            setDownloading(false);
        }
    };

    const handleRegistrationSubmit = async () => {
        const validationError = validateRegistration();
        if (validationError !== null) {
            setError(validationError);
            return;
        }

        setDownloading(true);
        setError(null);

        try {
            const signUpResult = await postJson('/api/v1/auth/sign-up', {
                email: trimmedEmail,
                password,
                agreementTermsOfService,
                agreementPrivacyPolicy,
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                phone: phone.replace(/\s+/g, ''),
            });

            if (signUpResult.response.status === 201 && signUpResult.data) {
                setCurrentIdentity(signUpResult.data);
                navigateAfterAuthentication(signUpResult.data);
                return;
            }

            const signInResult = await postJson('/api/v1/auth/sign-in', {
                email: trimmedEmail,
                password,
            });

            setCurrentIdentity(signInResult.data);
            navigateAfterAuthentication(signInResult.data);
        } catch (requestError) {
            setError(requestError);
        } finally {
            setDownloading(false);
        }
    };

    const resetToEmail = () => {
        setStep('email');
        setPassword('');
        setPasswordAgain('');
        setError(null);
    };

    const handleExternalAuthenticationSuccess = (identity) => {
        setCurrentIdentity(identity);
        navigateAfterAuthentication(identity);
    };

    const facebookSuccess = (accessToken) => {
        setDownloading(true);
        setError(null);

        new ExternalAuthenticationApi(authorityConfiguration)
            .facebookAccessTokenAuth({
                accessToken,
            })
            .then(handleExternalAuthenticationSuccess)
            .catch((requestError) => et(requestError).then((message) => { if (message) toast.error(message); }))
            .finally(() => setDownloading(false));
    };

    const googleSuccess = (code) => {
        setDownloading(true);
        setError(null);

        new ExternalAuthenticationApi(authorityConfiguration)
            .googleAccessTokenAuth({
                code,
            })
            .then(handleExternalAuthenticationSuccess)
            .catch((requestError) => et(requestError).then((message) => { if (message) toast.error(message); }))
            .finally(() => setDownloading(false));
    };

    return (
        <AuthLayout title={step === 'signup' ? t('auth.sign-up') : t('auth.sign-in')} authImage={AuthImage}>
            <LoadingComponent loading={state.downloading}>
                <FormContainer>
                    {state.error && <FormError error={state.error}/>}

                    <FormControl
                        label={t('auth.email-address')}
                        type={'email'}
                        value={email}
                        onChange={(event) => {
                            setEmail(event.target.value);
                            if (state.error) {
                                setError(null);
                            }
                        }}
                    />

                    {step === 'email' && (
                        <div className="auth-btn-wrap mt-4">
                            <FormButton className={'button-primary'} onClick={handleEmailSubmit}>
                                {t('onboarding.next-step')}
                            </FormButton>
                        </div>
                    )}

                    {step === 'login' && (
                        <>
                            <FormControl
                                className={'mt-4'}
                                label={t('auth.password')}
                                type={'password'}
                                value={password}
                                onChange={(event) => {
                                    setPassword(event.target.value);
                                    if (state.error) {
                                        setError(null);
                                    }
                                }}
                            />

                            <div className="auth-btn-wrap mt-4">
                                <FormButton className={'button-primary'} onClick={handleLoginSubmit}>
                                    {t('auth.sign-in')}
                                </FormButton>
                            </div>

                            <div className="mt-4 text-center">
                                <a
                                    href="#"
                                    className="font-medium text-indigo-500 underline hover:text-indigo-600 dark:hover:text-indigo-400"
                                    onClick={(event) => {
                                        event.preventDefault();
                                        resetToEmail();
                                    }}
                                >
                                    {t('onboarding.addon-change-email')}
                                </a>
                            </div>
                        </>
                    )}

                    {step === 'signup' && (
                        <>
                            <FormControl
                                className={'mt-4'}
                                label={t('user.first-name')}
                                value={firstName}
                                onChange={(event) => {
                                    setFirstName(event.target.value);
                                    if (state.error) {
                                        setError(null);
                                    }
                                }}
                            />
                            <FormControl
                                className={'mt-4'}
                                label={t('user.last-name')}
                                value={lastName}
                                onChange={(event) => {
                                    setLastName(event.target.value);
                                    if (state.error) {
                                        setError(null);
                                    }
                                }}
                            />
                            <FormControl
                                className={'mt-4'}
                                label={t('user.phone')}
                                value={phone}
                                onChange={(event) => {
                                    setPhone(event.target.value);
                                    if (state.error) {
                                        setError(null);
                                    }
                                }}
                            />
                            <FormControl
                                className={'mt-4'}
                                label={t('auth.password')}
                                type={'password'}
                                value={password}
                                onChange={(event) => {
                                    setPassword(event.target.value);
                                    if (state.error) {
                                        setError(null);
                                    }
                                }}
                            />
                            <FormControl
                                className={'mt-4'}
                                label={t('auth.password-again')}
                                type={'password'}
                                value={passwordAgain}
                                onChange={(event) => {
                                    setPasswordAgain(event.target.value);
                                    if (state.error) {
                                        setError(null);
                                    }
                                }}
                            />

                            <div className={'mt-4'}>
                                <FormCheckbox
                                    label={t('auth.agreed-with-tos-with-link', {link: 'https://datixo.com/documents/vseobecne-obchodni-podminky.pdf'})}
                                    checked={agreementTermsOfService}
                                    onChange={(event) => setAgreementTermsOfService(event.target.checked)}
                                />
                            </div>
                            <FormCheckbox
                                label={t('auth.agreed-with-privacy-with-link', {link: 'https://datixo.com/documents/vseobecne-obchodni-podminky.pdf'})}
                                checked={agreementPrivacyPolicy}
                                onChange={(event) => setAgreementPrivacyPolicy(event.target.checked)}
                            />

                            <div className="auth-btn-wrap mt-4">
                                <FormButton className={'button-primary'} onClick={handleRegistrationSubmit}>
                                    {t('auth.sign-up-button')}
                                </FormButton>
                            </div>

                            <div className="mt-4 text-center">
                                <a
                                    href="#"
                                    className="font-medium text-indigo-500 underline hover:text-indigo-600 dark:hover:text-indigo-400"
                                    onClick={(event) => {
                                        event.preventDefault();
                                        resetToEmail();
                                    }}
                                >
                                    {t('onboarding.addon-change-email')}
                                </a>
                            </div>
                        </>
                    )}
                </FormContainer>

                {step === 'email' && (
                    <>
                        <Or/>
                        <div className="comp__socialLogin">
                            <FacebookLoginButton
                                configurationId={import.meta.env.VITE_META_LOGIN_CONFIGURATION_ID}
                                onSuccess={facebookSuccess}
                                className={'mb-4'}
                            >
                                {t('auth.continue-with-facebook')}
                            </FacebookLoginButton>

                            <GoogleLoginButton
                                onSuccess={googleSuccess}
                                onError={(requestError) => et(requestError).then((message) => { if (message) toast.error(message); })}
                                className={'mb-3'}
                            >
                                {t('auth.continue-with-google')}
                            </GoogleLoginButton>
                        </div>
                    </>
                )}
            </LoadingComponent>
        </AuthLayout>
    );
}
